import React, { useState, useEffect, lazy, Suspense } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useAmazonContext } from "./Contexts/AmazonContext";
import { usePostHog } from 'posthog-js/react';
import Footer from "./components/Footer";
import Topic from "./components/Topic";
import Quiz from "./components/Quiz";
import Button from "./components/Button";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { BrowserRouter as Redirect } from "react-router-dom";
import "./App.css";
import MainSideNav from "./components/sidebar/MainSideNav";
import CourseMenuPage from "./components/landingPage/CourseMenuPage";
import CourseLandingPage from "./components/landingPage/CourseLandingPage";
import CourseStats from "./components/landingPage/CourseStats";
import LoginPage from "./components/userAuth/LoginPage";
import Logout from "./components/userAuth/Logout";
import LogoutMessage from "./components/userAuth/LogoutMessage";
import UniversalLogout from "./components/userAuth/UniversalLogout";
import UniversalLogoutMessage from "./components/userAuth/UniversalLogoutMessage";
import AccountDeleted from "./components/userAuth/AccountDeleted";
import SomethingWentWrong from "./components/userAuth/SomethingWentWrong";
import Lesson from "./components/Lesson";
import GetStarted from "./components/userAuth/GetStarted";
import WelcomeMessageOne from "./components/userAuth/WelcomeMessageOne";
import WelcomeMessageTwo from "./components/userAuth/WelcomeMessageTwo";
import WelcomeMessageThree from "./components/userAuth/WelcomeMessageThree";
import WelcomeMessageFour from "./components/userAuth/WelcomeMessageFour";
import LessonDemo from "./components/LessonDemo";
import WelcomeMessageFive from "./components/userAuth/WelcomeMessageFive";
import WelcomeMessageNotifications from "./components/userAuth/WelcomeMessageNotifications";
import WelcomeMessageCourses from "./components/userAuth/WelcomeMessageCourses";
import WelcomeMessageSeven from "./components/userAuth/WelcomeMessageSeven";
import { useHistory } from "react-router-dom";
import LessonEditor from "./components/lessonBuilder/LessonEditor";
import axios from "axios";
import CourseHomePage from "./components/landingPageNew/CourseHomePage";
import CourseSummaryPage from "./components/landingPageNew/CourseSummaryPage";
import Library from "./components/landingPageNew/Library";
import Profile from "./components/landingPageNew/Profile";
import "./global.css";
import AnnouncementAdmin from "./components/AnnouncementAdmin";
import SalaamList from "./components/Salaam/SalaamList";
import SalaamDetail from "./components/Salaam/SalaamDetail";
import SalaamSentList from "./components/Salaam/SalaamSentList";
import SalaamSentDetail from "./components/Salaam/SalaamSentDetail";
import ReactNativeLoginHandling from "./components/userAuth/ReactNativeLoginHandling";
import MarketingHeader from "./components/marketingPage/MarketingHeader";
import Leaderboard from "./components/landingPageNew/Leaderboard";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Support from "./components/Support";
import FeedbackForum from "./components/landingPageNew/FeedbackForum";
import UpdateModal from "./components/UpdateModal";
import api, { prefetch } from "./components/api";
import DailyTopPerformer from "./components/landingPageNew/DailyTopPerformer";
import StreakModal from "./components/StreakModal";
import StreakFrozenModal from "./components/streakFreeze/StreakFrozenModal";
import {
  markFrozenModalSeen,
  shouldAutoShowFrozenModal,
} from "./components/streakFreeze/streakFreezeUtils";
import StreakFreezePage from "./components/landingPageNew/StreakFreezePage";
import KnowledgeChallenge from "./components/knowledgeChallenge/KnowledgeChallenge";
import KnowledgeChallengeResults from "./components/knowledgeChallenge/KnowledgeChallengeResults";
import KnowledgeChallengeAdmin from "./components/knowledgeChallenge/KnowledgeChallengeAdmin";
import RouteErrorBoundary from "./components/ErrorBoundary";

// Admin-only screens, split out of the bundle every learner downloads. Dashboard and the
// two detail views each pull in recharts; Course Import is the book/course pipeline.
const Dashboard = lazy(() => import("./components/landingPageNew/Dashboard"));
const CourseImportPage = lazy(() =>
  import("./components/lessonBuilder/CourseImportPage")
);
const UserLessonsDetail = lazy(() =>
  import("./components/landingPageNew/UserLessonsDetail")
);
const UserProfileDetail = lazy(() =>
  import("./components/landingPageNew/UserProfileDetail")
);
const CourseCompletionsDetail = lazy(() =>
  import("./components/landingPageNew/CourseCompletionsDetail")
);

// PrivateRoute prevents accessibilty of contents if the user is not logged in
const PrivateRoute = ({
  component: Component,
  isLoggedIn,
  isTokenExpired,
  renewSession,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isLoggedIn && !isTokenExpired ? (
          <Component {...props} />
        ) : (
          <Redirect to="/" />
        )
      }
    />
  );
};

// AdminRoute prevents access to components if the user is not an admin
const AdminRoute = ({
  component: Component,
  isLoggedIn,
  userRole,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isLoggedIn && userRole === "admin" ? (
          <Component {...props} />
        ) : (
          <Redirect to="/" />
        )
      }
    />
  );
};

function App() {
  // Initialize states
  const {
    courseTitleUserIsOn,
    entryStore,
    isDarkMode,
    setIsDarkMode,
    userRole,
    setUserRole,
    navOpen,
    setNavOpen,
    showUpdateModal,
    setShowUpdateModal,
    updateInfo,
    setUpdateInfo,
    showCompletionModal,
    setShowCompletionModal,
    // Streak states
    showStreakModal,
    setShowStreakModal,
    currentStreak,
    setCurrentStreak,
    fetchUserStreak,
    streakStatus,
    streakRecovery,
    showStreakFrozenModal,
    setShowStreakFrozenModal,
    streakRestoredFrom,
    setStreakRestoredFrom,
    // Lessons states
    totalLessons,
    setTotalLessons,
    fetchUserLessons,
  } = useAmazonContext();

  // Tell returning users their streak is frozen, once per day while the freeze lasts
  useEffect(() => {
    if (streakStatus !== "frozen" || !streakRecovery) return;
    if (window.location.pathname === "/") return;
    if (!shouldAutoShowFrozenModal(streakRecovery)) return;
    markFrozenModalSeen(streakRecovery);
    setShowStreakFrozenModal(true);
  }, [streakStatus, streakRecovery?.expiresOn]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [tokenExpiration, setTokenExpiration] = useState(null);
  const history = useHistory();
  const posthog = usePostHog();

  // Function to check if the token is expired
  const isTokenExpired = () => {
    return tokenExpiration && Date.now() > tokenExpiration;
  };

  console.log(userRole);

  useEffect(() => {
    const restoreSession = async () => {
      // Check localStorage for authentication state and expiration time
      const storedIsLoggedIn = localStorage.getItem("isLoggedIn");
      const storedTokenExpiration = localStorage.getItem("tokenExpiration");

      if (storedIsLoggedIn && storedTokenExpiration) {
        // If authentication state and expiration time are found, check if token is still valid
        const expirationTime = parseInt(storedTokenExpiration);
        if (expirationTime > Date.now()) {
          setIsLoggedIn(true);
          setTokenExpiration(expirationTime);

          // Identify user in PostHog for all logins (web and React Native)
          const userEmail = localStorage.getItem("userEmail");
          const userGivenName = localStorage.getItem("userGivenName");
          const userFamilyName = localStorage.getItem("userFamilyName");

          if (userEmail) {
            try {
              await api.get("/fetchUserRole", { params: { userEmail } });
            } catch (error) {
              const status = error?.response?.status;
              if (status === 404 || status === 500) {
                localStorage.clear();
                setIsLoggedIn(false);
                setUserRole(null);
                window.location.replace("/something-went-wrong");
                return;
              }
            }
          }

          if (userEmail && posthog) {
            console.log('🔥 Identifying user in PostHog:', userEmail);
            posthog.identify(userEmail, {
              email: userEmail,
              name: `${userGivenName} ${userFamilyName}`,
              given_name: userGivenName,
              family_name: userFamilyName,
              platform: window.ReactNativeAuth ? 'react-native' : 'web',
            });
          }
        } else {
          // Token has expired, clear localStorage
          localStorage.removeItem("isLoggedIn");
          localStorage.removeItem("tokenExpiration");
        }
      }

      //fetch user role from local and set state:
      setUserRole(localStorage.getItem("userRole"));

      checkUpdateStatus();

      // Fetch user streak and lesson data if logged in
      if (localStorage.getItem("isLoggedIn")) {
        fetchUserStreak();
        fetchUserLessons();

        // Warm the caches for the main tabs in the background so the first tap on Home,
        // Library, Leaderboard or Salaam renders from cache instead of waiting on the network.
        const email = localStorage.getItem("userEmail");
        prefetch("/coursesData", { ttl: 300000 });
        prefetch("/leaderboard");
        if (email) {
          prefetch("/api/announcements", { params: { email }, ttl: 30000 });
          prefetch("/fetchProgressRecord", { params: { userEmail: email } });
          prefetch("/fetchChapterProgressRecord", { params: { userEmail: email } });
        }
      }
    };

    restoreSession();
  }, [posthog, isLoggedIn]);

  const checkUpdateStatus = async () => {
    try {
      // Don't show modal on landing page
      if (window.location.pathname === "/") {
        return;
      }

      const userEmail = localStorage.getItem("userEmail");
      console.log("Checking update status for:", userEmail);

      const response = await api.get("/checkUpdateStatus", {
        params: { userEmail },
      });

      console.log("Update status response:", response.data);

      if (response.data.showUpdate) {
        setUpdateInfo(response.data);
        setShowUpdateModal(true);
      }
    } catch (error) {
      console.error("Error checking update status:", error);
    }
  };

  const handleUpdate = async () => {
    try {
      const userEmail = localStorage.getItem("userEmail");
      await api.post("/trackUpdateClick", {
        userEmail,
      });

      // Close the update modal
      setShowUpdateModal(false);

      // Set a timeout to show completion modal after 3 seconds
      setTimeout(() => {
        setShowCompletionModal(true);
      }, 3000);

      // Open Play Store in new tab
      window.open(
        "https://play.google.com/store/apps/details?id=com.anonymous.hhcourses",
        "_blank"
      );
    } catch (error) {
      console.error("Error tracking update click:", error);
    }
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
  };

  const handleCompleteClick = () => {
    setShowCompletionModal(false);
  };

  // Add a class to the body element based on the dark mode state
  document.body.className = isDarkMode ? "darkbody" : "lightbody";

  return (
    // <div>
    //   <Router>
    //     <Switch>
    //       <MarketingHeader />
    //     </Switch>
    //   </Router>
    // </div>
    <div className="App">
      {/* Router and routes */}
      <Router>
        <RouteErrorBoundary>
        <Suspense fallback={null}>
        <Switch>
          <Route path="/" exact component={MarketingHeader} />
          <Route path="/privacy-policy" exact component={PrivacyPolicy} />
          <Route path="/support" exact component={Support} />

          <AdminRoute
            path="/course-import"
            exact
            component={CourseImportPage}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />

          <AdminRoute
            path="/lesson-builder"
            exact
            component={CourseImportPage}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />

          <AdminRoute
            path="/course-editor"
            exact
            component={LessonEditor}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />

          <AdminRoute
            path="/lesson-editor"
            exact
            component={LessonEditor}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />

          <AdminRoute
            path="/announcement-admin"
            exact
            component={AnnouncementAdmin}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />
          <AdminRoute
            path="/announcement-admin/sent"
            exact
            component={SalaamSentList}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />
          <AdminRoute
            path="/announcement-admin/sent/:id"
            component={SalaamSentDetail}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />

          <Route
            path="/home"
            exact
            render={() => (isLoggedIn ? <CourseHomePage /> : <GetStarted />)}
          />

          <Route
            path="/LogoutMessage"
            exact
            render={() => <LogoutMessage setIsLoggedIn={setIsLoggedIn} />}
          />
          <Route
            path="/UniversalLogoutMessage"
            exact
            render={() => <UniversalLogoutMessage setIsLoggedIn={setIsLoggedIn} />}
          />
          <Route
            path="/account-deleted"
            exact
            component={AccountDeleted}
          />
          <Route
            path="/something-went-wrong"
            exact
            component={SomethingWentWrong}
          />
          <Route
            path="/LoginPage"
            exact
            render={() =>
              isLoggedIn ? (
                <CourseHomePage />
              ) : (
                <LoginPage
                  setIsLoggedIn={setIsLoggedIn}
                  setTokenExpiration={setTokenExpiration}
                />
              )
            }
          />
          <Route
            path="/ReactNativeLoginHandling"
            exact
            render={() =>
              isLoggedIn ? (
                <CourseHomePage />
              ) : (
                <ReactNativeLoginHandling
                  setIsLoggedIn={setIsLoggedIn}
                  setTokenExpiration={setTokenExpiration}
                />
              )
            }
          />

          <Route
            path="/WelcomeMessageOne"
            exact
            component={WelcomeMessageOne}
          />
          <Route
            path="/WelcomeMessageTwo"
            exact
            component={WelcomeMessageTwo}
          />
          <Route
            path="/WelcomeMessageThree"
            exact
            component={WelcomeMessageThree}
          />
          <Route
            path="/WelcomeMessageFour"
            exact
            component={WelcomeMessageFour}
          />
          <Route
            path="/WelcomeMessageFive"
            exact
            component={WelcomeMessageFive}
          />
          <Route
            path="/WelcomeMessageNotifications"
            exact
            component={WelcomeMessageNotifications}
          />
          <Route
            path="/WelcomeMessageSeven"
            exact
            component={WelcomeMessageSeven}
          />
          <Route
            path="/WelcomeMessageCourses"
            exact
            component={WelcomeMessageCourses}
          />
          <Route path="/LoginPage" exact component={LoginPage} />
          <Route path="/LessonDemo" exact component={LessonDemo} />

          {/* Protected Routes */}
          <PrivateRoute
            path="/"
            exact
            component={CourseHomePage}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/library"
            exact
            component={Library}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/profile"
            exact
            component={Profile}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/dashboard"
            exact
            component={Dashboard}
            isLoggedIn={isLoggedIn}
          />
          <AdminRoute
            path="/streak-freeze"
            exact
            component={StreakFreezePage}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />
          <AdminRoute
            path="/knowledge-challenge-admin"
            exact
            component={KnowledgeChallengeAdmin}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />
          <PrivateRoute
            path="/knowledge-challenge"
            exact
            component={KnowledgeChallenge}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path={[
              "/knowledge-challenge/results/:challengeId",
              "/knowledge-challenge/results",
            ]}
            exact
            component={KnowledgeChallengeResults}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/user-lessons-detail"
            exact
            component={UserLessonsDetail}
            isLoggedIn={isLoggedIn}
          />
          <AdminRoute
            path="/user-profile-detail"
            exact
            component={UserProfileDetail}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
          />
          <PrivateRoute
            path="/course-completions-detail"
            exact
            component={CourseCompletionsDetail}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/daily-top-performer"
            exact
            component={DailyTopPerformer}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/feedback-forum"
            exact
            component={FeedbackForum}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/:courseTitle/topic/:topicNumber"
            component={Topic}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/:courseTitle/quiz/:topicNumber"
            component={Quiz}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/leaderboard"
            exact
            component={Leaderboard}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/salaam"
            exact
            component={SalaamList}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/salaam/:id"
            component={SalaamDetail}
            isLoggedIn={isLoggedIn}
          />
          <Route path="/:courseTitle" exact component={CourseSummaryPage} />
          <PrivateRoute
            path="/:courseTitle/start"
            exact
            component={CourseLandingPage}
            isLoggedIn={isLoggedIn}
          />
          <PrivateRoute
            path="/:courseTitle/lesson/:lessonNumber"
            component={Lesson}
            isLoggedIn={isLoggedIn}
          />
        </Switch>
        </Suspense>
        </RouteErrorBoundary>
      </Router>

      {showUpdateModal && updateInfo && !showCompletionModal && (
        <UpdateModal
          message={updateInfo.message}
          isMandatory={updateInfo.isMandatory}
          onUpdate={handleUpdate}
          onClose={handleCloseUpdateModal}
          isComplete={false}
        />
      )}

      {showCompletionModal && (
        <UpdateModal isComplete={true} onCompleteClick={handleCompleteClick} />
      )}

      {/* Streak Modal */}
      <StreakModal
        isOpen={showStreakModal}
        onClose={() => {
          setShowStreakModal(false);
          setStreakRestoredFrom(null);
        }}
        streakCount={currentStreak}
        restoredFrom={streakRestoredFrom}
      />

      <StreakFrozenModal
        isOpen={showStreakFrozenModal && streakStatus === "frozen"}
        onClose={() => setShowStreakFrozenModal(false)}
        recovery={streakRecovery}
      />
    </div>
  );
}

export default App;
